import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { AxiosRequestConfig } from 'axios';
import { apiClient } from '@/lib/api/client';

export interface DashboardCustomer {
  customer_id: string;
  customer_name: string;
  collection_status: 'pending' | 'running' | 'ready' | 'failed';
  last_collected_at: string | null;
  collection_error: string | null;
}

const DashboardCustomerContext = createContext<DashboardCustomer | null>(null);

export function DashboardCustomerProvider({ customer, children }: { customer: DashboardCustomer; children: ReactNode }) {
  return <DashboardCustomerContext.Provider value={customer}>{children}</DashboardCustomerContext.Provider>;
}

export function useDashboardCustomer() {
  const customer = useContext(DashboardCustomerContext);
  if (!customer) throw new Error('A dashboard customer must be selected');
  return customer;
}

// Bind each request to the rendered customer, including delayed modal requests.
// Do not change global axios defaults: old requests can finish after a switch.
export function useCustomerApi() {
  const { customer_id } = useDashboardCustomer();
  return useMemo(() => ({
    get: <T = any,>(url: string, config?: AxiosRequestConfig) => apiClient.get<T>(url, {
      ...config,
      params: { ...config?.params, customer_id },
    }),
  }), [customer_id]);
}
